import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useTheme, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  BookOpen, 
  Sun, 
  Moon, 
  LogOut, 
  Sparkles, 
  Mountain, 
  Feather,
  ChevronRight,
  Clock,
  Flame,
  Target,
  Trophy,
  Star,
  Crown,
  Award,
  Bookmark,
  Map,
  Library,
  Shield,
  MessageSquare,
  Circle,
  Triangle,
  Square
} from "lucide-react";
import { KITT } from "@/constants/hedgekit";
import FeedbackModal from "@/components/FeedbackModal";
import Tutorial from "@/components/Tutorial";

const ICON_MAP = {
  "book-open": BookOpen,
  "flame": Flame,
  "trophy": Trophy,
  "crown": Crown,
  "star": Star,
  "bookmark": Bookmark,
  "map": Map,
  "library": Library,
  "target": Target,
  "award": Award,
};

// New reading levels (replacing bands)
const LEVELS = [
  {
    id: "1",
    name: "Level 1 - Easy",
    description: "Perfect for early readers. Simple stories and fairy tales.",
    icon: Circle,
    className: "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800",
    span: "col-span-1",
    iconColor: "text-green-600 dark:text-green-400"
  },
  {
    id: "2",
    name: "Level 2 - Medium",
    description: "For growing readers. More complex plots and vocabulary.",
    icon: Triangle,
    className: "bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200 dark:border-yellow-800",
    span: "col-span-1 md:col-span-2",
    iconColor: "text-yellow-600 dark:text-yellow-400"
  },
  {
    id: "3",
    name: "Level 3 - Challenge",
    description: "For strong readers. Longer stories with rich language.",
    icon: Square,
    className: "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800",
    span: "col-span-1 md:col-span-3",
    iconColor: "text-blue-600 dark:text-blue-400"
  }
];

// Keep old bands for backwards compatibility with progress
const BANDS = [
  { id: "short-simple", name: "Short & Simple" },
  { id: "regular-chapter", name: "Regular Chapter" },
  { id: "big-challenge", name: "Big Challenge" }
];

export default function Home() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [continueReading, setContinueReading] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState(true);
  const [gamificationStats, setGamificationStats] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  // Check if should show tutorial for new users
  useEffect(() => {
    if (localStorage.getItem('kbn_show_tutorial') === 'true') {
      setShowTutorial(true);
    }
  }, []);

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('kbn_session_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchProgress();
    fetchGamificationStats();
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await fetch(`${API}/progress`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setContinueReading(data.slice(0, 3)); // Show last 3 books
      }
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    } finally {
      setLoadingProgress(false);
    }
  };

  const fetchGamificationStats = async () => {
    try {
      const response = await fetch(`${API}/gamification/stats`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setGamificationStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch gamification stats:', error);
    }
  };

  const handleLevelClick = (levelId) => {
    navigate(`/library/${levelId}`);
  };

  const handleContinueReading = (bookId, band) => {
    navigate(`/read/${bookId}`, { state: { band } });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const streak = gamificationStats?.streak || {};
  const goal = gamificationStats?.daily_goal || {};
  const achievements = gamificationStats?.achievements || [];
  const unlockedAchievements = achievements.filter(a => a.unlocked).slice(0, 5); // Show up to 5 badges
  
  const goalProgress = goal.daily_sentences_goal > 0 
    ? Math.min(100, (goal.today_sentences / goal.daily_sentences_goal) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-background paper-texture">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-primary" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-bold text-xl">Story Bands</span>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full"
              data-testid="theme-toggle-home"
            >
              {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="rounded-full p-1" data-testid="user-menu">
                  <Avatar className="w-9 h-9">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-primary/10 text-primary font-bold">
                      {user?.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                {user?.email === "shepromptsja@gmail.com" && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/admin')}>
                      <Shield className="w-4 h-4 mr-2" />
                      Admin Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Welcome */}
        <div className="mb-8 animate-slide-up">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-black mb-3">
            Choose your reading vibe
          </h1>
          <p className="text-muted-foreground text-lg">
            Pick what feels right for you today, {user?.name?.split(' ')[0]}!
          </p>
        </div>

        {/* Streak & Goal Widget */}
        <section className="mb-8 animate-slide-up" style={{ animationDelay: '0.05s' }}>
          <button
            onClick={() => navigate('/stats')}
            className="w-full bg-gradient-to-r from-orange-100 via-amber-50 to-green-50 dark:from-orange-950/40 dark:via-amber-950/30 dark:to-green-950/40 rounded-2xl p-4 hover:scale-[1.01] transition-all border border-orange-200/50 dark:border-orange-800/30"
            data-testid="streak-widget"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <img 
                  src={KITT.thumbsUp} 
                  alt="Kitt" 
                  className="w-12 h-12 object-contain"
                />
                <div className="text-left">
                  <p className="font-heading font-bold text-lg">
                    {streak.current_streak || 0} day streak
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {streak.total_sentences || 0} sentences read
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right hidden sm:block">
                  <p className="text-sm text-muted-foreground">Today's goal</p>
                  <div className="flex items-center gap-2">
                    <Progress value={goalProgress} className="w-20 h-2" />
                    <span className="text-xs font-medium">
                      {goal.today_sentences || 0}/{goal.daily_sentences_goal || 20}
                    </span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </div>
            
            {/* Achievement badges */}
            {unlockedAchievements.length > 0 && (
              <div className="flex items-center gap-2 pt-3 border-t border-orange-200/50 dark:border-orange-800/30">
                <span className="text-xs text-muted-foreground mr-1">Badges:</span>
                {unlockedAchievements.map((ach) => {
                  const Icon = ICON_MAP[ach.icon] || Star;
                  return (
                    <div
                      key={ach.id}
                      className="w-8 h-8 bg-amber-400 dark:bg-amber-600 rounded-full flex items-center justify-center"
                      title={ach.name}
                    >
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                  );
                })}
                {gamificationStats?.unlocked_count > 5 && (
                  <span className="text-xs text-muted-foreground ml-1">
                    +{gamificationStats.unlocked_count - 5} more
                  </span>
                )}
              </div>
            )}
          </button>
        </section>

        {/* Continue Reading Section */}
        {!loadingProgress && continueReading.length > 0 && (
          <section className="mb-10 animate-slide-up" style={{ animationDelay: '0.1s' }}>
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-heading font-bold">Continue Reading</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {continueReading.map((book) => (
                <button
                  key={book.book_id}
                  onClick={() => handleContinueReading(book.book_id, book.band)}
                  className="bg-card border border-border rounded-2xl p-4 text-left hover:shadow-card hover:scale-[1.02] transition-all duration-300 group"
                  data-testid={`continue-book-${book.book_id}`}
                >
                  <p className="font-heading font-bold text-foreground line-clamp-1 mb-1">
                    {book.book_title}
                  </p>
                  <p className="text-sm text-muted-foreground mb-2">{book.book_author}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs bg-muted px-2 py-1 rounded-full">
                      {BANDS.find(b => b.id === book.band)?.name.split(' ')[0] || 'Reading'}
                    </span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Level Selection Grid */}
        <section>
          <h2 className="text-xl font-heading font-bold mb-4">Start Something New</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {LEVELS.map((level, index) => {
              const Icon = level.icon;
              return (
                <button
                  key={level.id}
                  onClick={() => handleLevelClick(level.id)}
                  className={`${level.span} ${level.className} border-2 rounded-3xl p-6 sm:p-8 text-left hover:scale-[1.02] hover:shadow-card transition-all duration-300 animate-slide-up group`}
                  style={{ animationDelay: `${0.2 + index * 0.1}s` }}
                  data-testid={`level-${level.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-14 h-14 bg-white/50 dark:bg-black/20 rounded-2xl flex items-center justify-center`}>
                      <Icon className={`w-7 h-7 ${level.iconColor}`} strokeWidth={2.5} />
                    </div>
                    <ChevronRight className="w-6 h-6 text-muted-foreground group-hover:text-foreground group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-heading font-bold mb-2 text-foreground">
                    {level.name}
                  </h3>
                  <p className="text-muted-foreground text-sm sm:text-base">
                    {level.description}
                  </p>
                </button>
              );
            })}
          </div>
        </section>
        
        {/* Footer with affiliate link */}
        <footer className="mt-12 mb-20 sm:mb-8 text-center space-y-4">
          <Button 
            variant="outline" 
            onClick={() => setShowFeedback(true)}
            className="mx-auto"
            data-testid="feedback-button"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Send Feedback
          </Button>
          
          <div>
            <a
              href="https://get.emergent.sh/xjnotg1y8n0g"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20 text-sm font-medium text-purple-600 dark:text-purple-400 hover:from-purple-500/20 hover:to-blue-500/20 transition-all"
            >
              <span>✨</span>
              Build Your Own App with Emergent
            </a>
            <p className="mt-2 text-[10px] text-muted-foreground/40">
              This site contains affiliate links. We may earn a commission at no extra cost to you.
            </p>
          </div>
        </footer>
      </main>
      
      {/* Feedback Modal */}
      <FeedbackModal open={showFeedback} onOpenChange={setShowFeedback} />
      
      {/* Tutorial for new users */}
      {showTutorial && <Tutorial onComplete={() => setShowTutorial(false)} />}
    </div>
  );
}
